import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, Globe, ChevronDown, Flower2 } from "lucide-react";
import { useT } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";

function LangSwitcher() {
  const { lang, switchLang } = useT();
  return (
    <div className="inline-flex items-center rounded-full border border-stone-300 overflow-hidden text-sm" data-testid="language-toggle">
      <button
        data-testid="language-toggle-en"
        onClick={() => switchLang("en")}
        className={`px-3 py-1 font-medium transition-colors ${lang === "en" ? "bg-slate-brand text-white" : "text-stone-600 hover:bg-stone-100"}`}
      >EN</button>
      <button
        data-testid="language-toggle-ta"
        onClick={() => switchLang("ta")}
        className={`px-3 py-1 font-tamil transition-colors ${lang === "ta" ? "bg-slate-brand text-white" : "text-stone-600 hover:bg-stone-100"}`}
      >தமிழ்</button>
      <button
        data-testid="language-toggle-hi"
        onClick={() => switchLang("hi")}
        className={`px-3 py-1 transition-colors ${lang === "hi" ? "bg-slate-brand text-white" : "text-stone-600 hover:bg-stone-100"}`}
      >हिं</button>
    </div>
  );
}

export function Header() {
  const { t } = useT();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const links = [
    { to: "/discover", label: t("nav.discover") },
    { to: "/stories", label: t("nav.stories") },
    { to: "/for-csr", label: t("nav.forCsr") },
    { to: "/for-ngos", label: t("nav.forNgos") },
    { to: "/verification", label: t("nav.verification") },
    { to: "/about", label: t("nav.about") },
    { to: "/contact", label: t("nav.contact") },
  ];

  const dashPath = user?.role === "admin" ? "/admin" : user?.role === "csr_manager" ? "/csr/workspace" : user?.role === "ngo_admin" ? "/ngo/dashboard" : "/discover";
  const dashLabel = user?.role === "admin" ? t("nav.console") : user?.role === "csr_manager" ? t("nav.workspace") : t("nav.dashboard");

  const onLogout = async () => { await logout(); navigate("/"); };

  return (
    <header className="sticky top-0 z-50 bg-stone-50/95 backdrop-blur-md border-b border-stone-200 shadow-sm">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-4">
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="brand-logo">
          <span className="flex items-center justify-center w-9 h-9 rounded-lg bg-slate-brand text-gold-light"><Flower2 size={20} /></span>
          <div className="leading-none">
            <div className="font-extrabold text-slate-brand text-sm sm:text-base whitespace-nowrap">{t("brand.name")}</div>
            <div className="text-[10px] text-stone-500 hidden sm:block">{t("brand.tag")}</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace(/\//g, "")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-md text-sm font-medium transition-colors ${isActive ? "text-terracotta bg-terracotta-light" : "text-stone-700 hover:text-terracotta hover:bg-stone-100"}`}
            >{l.label}</NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <LangSwitcher />
          <div className="hidden md:flex items-center gap-2">
            {user ? (
              <>
                <Button asChild variant="outline" size="sm" data-testid="header-dashboard-btn">
                  <Link to={dashPath}>{dashLabel}</Link>
                </Button>
                <Button variant="ghost" size="sm" onClick={onLogout} data-testid="header-logout-btn">{t("common.logout")}</Button>
              </>
            ) : (
              <>
                <Button asChild variant="ghost" size="sm" data-testid="header-login-btn"><Link to="/login">{t("common.login")}</Link></Button>
                <Button asChild size="sm" className="bg-terracotta hover:bg-terracotta-hover" data-testid="header-register-btn"><Link to="/register">{t("common.register")}</Link></Button>
              </>
            )}
          </div>
          <button className="lg:hidden p-2 text-stone-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-stone-200 bg-stone-50 px-4 py-3 space-y-1">
          {links.map((l) => (
            <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
              className="block px-3 py-2 rounded-md text-sm font-medium text-stone-700 hover:bg-stone-100">{l.label}</NavLink>
          ))}
          <div className="pt-2 border-t border-stone-200 flex flex-col gap-2">
            {user ? (
              <>
                <Link to={dashPath} onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-medium text-terracotta">{dashLabel}</Link>
                <button onClick={() => { setOpen(false); onLogout(); }} className="px-3 py-2 text-sm text-left text-stone-700">{t("common.logout")}</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-medium text-stone-700">{t("common.login")}</Link>
                <Link to="/register" onClick={() => setOpen(false)} className="px-3 py-2 text-sm font-medium text-terracotta">{t("common.register")}</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

export default Header;
